import { APIProvider, InfoWindow, Map, Marker, useMap } from '@vis.gl/react-google-maps'
import React, { useEffect, useMemo, useState } from 'react'
import { CityEntityOffice, OfficeAddressDetailsEntity } from '../../../interface/offices'
import { APIResponseEntity } from '../../../interface/api-responce'
import { AxiosResponse } from 'axios'
import { APIToGetAllOfficeAddresses, APItoGetAllOfficeRegions } from '../../../service/api-service'
import { toast } from "react-toastify";
import { toastOptObj } from '../../../utils/constants'
import { IsNullOrEmptyArray } from '../../../utils/null-check'
import { Dropdown } from 'primereact/dropdown'
import { MultiSelect } from 'primereact/multiselect'
import Loader from '../../../components/loader'
import { generateOfficeColor, handleDateTimeFormat } from '../../../utils/shares'
import { GoogleMapPlaceTypeEnumEntity, OfficeMarkerAddressTypeEnumEntity } from '../../../utils/enum'
import OfficeCoverageOverviewFeatureLayer from './office-coverage-overview-feature-layer'
import { useNavigate } from 'react-router-dom'

const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''
const mapId = process.env.REACT_APP_GOOGLE_MAPS_MAPID || ''

const FitSelectedOfficeOnMap = ({
    regions
}: {
    regions: CityEntityOffice[];
}) => {
    const map = useMap();

    useEffect(() => {
        if (!map || regions.length === 0) return;

        const bounds = new google.maps.LatLngBounds();

        regions.forEach((region) => {
            if (region.viewport && region.viewport.southwest && region.viewport.northeast
                && region.viewport.southwest.lat && region.viewport.southwest.lng
                && region.viewport.northeast.lat && region.viewport.northeast.lng
            ) {
                bounds.union(
                    new google.maps.LatLngBounds(
                        region.viewport.southwest,
                        region.viewport.northeast
                    )
                );
            } else if (region.location && region.location.lat && region.location.lng) {
                bounds.extend(region.location);
            }
        });

        if (!bounds.isEmpty()) {
            map.fitBounds(bounds, 24);
        }
    }, [map, regions]);
    return null;
};

const OfficeCoverageOverview = () => {
    const sectionRef = React.useRef<HTMLDivElement>(null);
    const officeMultiselectRef = React.useRef<any>(null);
    const addressTypeMultiselectRef = React.useRef<any>(null);
    const [selectedOffice, setSelectedOffice] = useState<OfficeAddressDetailsEntity | null>(null);
    const [officeRegionDetails, setofficeRegionDetails] = useState<CityEntityOffice[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isFetchingOfficeAddress, setIsFetchingOfficeAddress] = useState<boolean>(false);
    const [tempOffices, setTempOffices] = useState<string[]>([]);
    const [selectedOfficesFromDDL, setSelectedOfficesFromDDL] = useState<string[]>([]);
    const [showTerritories, setShowTerritories] = useState<boolean>(true);
    const [officeAddresses, setOfficeAddresses] = useState<OfficeAddressDetailsEntity[]>([]);
    const [addressType, setAddressType] = useState<OfficeMarkerAddressTypeEnumEntity[]>([OfficeMarkerAddressTypeEnumEntity.SHOWROOM_ADDRESS]);
    const [tempAddressType, setTempAddressType] = useState<OfficeMarkerAddressTypeEnumEntity[]>([OfficeMarkerAddressTypeEnumEntity.SHOWROOM_ADDRESS]);

    const navigate = useNavigate();

    useEffect(() => {
        const fetchOfficeAddresses = async (): Promise<void> => {
            setIsFetchingOfficeAddress(true);
            try {
                const resp: AxiosResponse<APIResponseEntity> = await APIToGetAllOfficeAddresses();
                if (resp && resp.status === 200) {
                    const axiosResp: APIResponseEntity = resp.data;
                    if (axiosResp.data && axiosResp.statusCode === 200 && axiosResp.data.officeAddresses && !IsNullOrEmptyArray(axiosResp.data.officeAddresses)) {
                        const officeAddresses = axiosResp.data.officeAddresses;
                        setOfficeAddresses(officeAddresses);
                    }
                }
            } catch (error) {
                console.log(error);
                toast.error("Failed to fetch office addresses", { ...toastOptObj });
            } finally {
                setIsFetchingOfficeAddress(false);
            }
        }

        fetchOfficeAddresses();
    }, []);

    useEffect(() => {
        fetchAllOfficeRegions();
    }, []);

    const officesForLegend = useMemo(() => {
        const map = new globalThis.Map<
            string,
            { officeId: string; officeName: string, cityName: string | null, stateName: string | null, countryName: string | null }
        >();

        if (officeRegionDetails && !IsNullOrEmptyArray(officeRegionDetails)) {
            officeRegionDetails.forEach(a => {
                if (a.officeId && !map.has(a.officeId)) {
                    map.set(a.officeId, {
                        officeId: a.officeId,
                        officeName: a.officeName ?? a.officeId,
                        cityName: a.officeCityName ?? null,
                        stateName: a.officeStateName ?? null,
                        countryName: a.officeCountryName ?? null
                    });
                }
            });
        }

        return Array.from(map.values());
    }, [officeRegionDetails]);


    useEffect(() => {
        if (officesForLegend && !IsNullOrEmptyArray(officesForLegend)) {
            const officeIds = officesForLegend.map((office) => office.officeId);

            setSelectedOfficesFromDDL(officeIds);
            setTempOffices(officeIds);
        }
    }, [officesForLegend]);

    const fetchAllOfficeRegions = async (): Promise<void> => {
        setIsLoading(true);
        const resp: AxiosResponse<APIResponseEntity> = await APItoGetAllOfficeRegions();
        setIsLoading(false);
        if (resp && resp.status === 200) {
            const axiosResp: APIResponseEntity = resp.data;
            if (axiosResp.data && axiosResp.statusCode === 200 && axiosResp.data.distributedRegions && !IsNullOrEmptyArray(axiosResp.data.distributedRegions)) {
                setofficeRegionDetails(axiosResp.data.distributedRegions);
            } else {
                toast.error(axiosResp.message, { ...toastOptObj });
            }
        } else {
            toast.error(resp && resp.data && resp.data.message ? resp.data.message : "Failed to fetch office details", {
                ...toastOptObj,
            });
        }
    };

    const waitForMapRender = async (mapElement: HTMLElement) => {
        const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
        const waitForFrame = () => new Promise(resolve => requestAnimationFrame(resolve));

        window.dispatchEvent(new Event('resize'));
        await waitForFrame();
        await waitForFrame();

        const mapImages = Array.from(mapElement.querySelectorAll('img'));
        await Promise.race([
            Promise.all(mapImages.map(img => {
                if (img.complete) return Promise.resolve();

                return new Promise(resolve => {
                    img.addEventListener('load', resolve, { once: true });
                    img.addEventListener('error', resolve, { once: true });
                });
            })),
            wait(1200)
        ]);

        window.dispatchEvent(new Event('resize'));
        await waitForFrame();
        await wait(300);
    };

    const handleExport = async () => {
        if (!sectionRef.current) return;

        const element = sectionRef.current;
        const mapPageElement = element.querySelector('.office-print-map-page') as HTMLElement | null;
        const detailsPageElement = element.querySelector('.office-print-details-page') as HTMLElement | null;
        if (!mapPageElement || !detailsPageElement) return;

        document.getElementById('print-style')?.remove();
        document.getElementById('office-print-root')?.remove();

        const { width, height } = mapPageElement.getBoundingClientRect();

        const PADDING = 24;
        const PRINT_PAGE_WIDTH = 794;

        const printableMapWidth = Math.min(width, PRINT_PAGE_WIDTH - PADDING * 2);
        const paddedW = printableMapWidth + PADDING * 2;
        const MAP_PRINT_SCALE = width > 0 ? printableMapWidth / width : 1;
        const printMapH = Math.max(height * MAP_PRINT_SCALE, 0);

        const printedAt = handleDateTimeFormat(new Date().toISOString());

        const singleOfficeName = selectedOfficesFromDDL.length === 1
            ? (officesForLegend.find(o => o.officeId === selectedOfficesFromDDL[0])?.officeName || '')
            : '';
        const headerTitle = singleOfficeName
            ? `Office Territories &#8212; ${singleOfficeName}`
            : 'Office Territories';

        const HEADER_H = 44;
        const FOOTER_H = 32;

        const style = document.createElement('style');
        style.id = 'print-style';
        style.innerHTML = `
        @media print {
            @page {
                size: portrait;  
                margin: 0 !important;
                margin-header: 0 !important;
                margin-footer: 0 !important;
            }
            html, body {
                margin: 0 !important;
                padding: 0 !important;
                width: ${paddedW}px !important;
                height: auto !important;
                overflow: visible !important;
            }
            body.office-coverage-printing > * {
                visibility: hidden !important;
            }
            body.office-coverage-printing .container,
            body.office-coverage-printing .container-fluid,
            body.office-coverage-printing .row,
            body.office-coverage-printing [class*="col-"] {
                margin: 0 !important;
                padding: 0 !important;
                max-width: none !important;
                width: auto !important;
            }
            body.office-coverage-printing #print-section,
            body.office-coverage-printing #print-section * {
                visibility: visible !important;
            }
            body.office-coverage-printing #print-section {
                display: block !important;
                position: fixed !important;
                left: 0 !important;
                top: ${HEADER_H}px !important;
                width: ${paddedW}px !important;
                height: ${1123 - HEADER_H - FOOTER_H}px !important;
                overflow: hidden !important;
                margin: 0 !important;
                padding: 0 !important;
                background: #ffffff !important;
                transform: none !important;
                zoom: 1 !important;
            }
            body.office-coverage-printing #print-section .office-print-map-page,
            body.office-coverage-printing #print-section .office-print-details-page,
            body.office-coverage-printing #print-section .office-print-areas-page {
                box-sizing: border-box !important;
                width: ${paddedW}px !important;
                padding: ${PADDING}px !important;
                background: #ffffff !important;
            }
            body.office-coverage-printing #print-section .office-print-map-page {
                display: block !important;
                height: ${printMapH + PADDING + 18}px !important;
                overflow: hidden !important;
                padding-bottom: 18px !important;
            }
            body.office-coverage-printing #print-section .office-print-map-page > div {
                box-sizing: border-box !important;
                width: ${width}px !important;
                height: ${height}px !important;
                transform: scale(${MAP_PRINT_SCALE}) !important;
                transform-origin: top left !important;
            }
            body.office-coverage-printing #print-section .office-print-details-page {
                display: block !important;
                position: static !important;
                left: auto !important;
                top: auto !important;
                width: ${paddedW}px !important;
                height: auto !important;
                max-height: none !important;
                min-height: 0 !important;
                padding-top: ${PADDING}px !important;
                padding-bottom: ${PADDING + FOOTER_H}px !important;
                overflow: visible !important;
                opacity: 1 !important;
                pointer-events: auto !important;
                z-index: auto !important;
                color: #111827 !important;
                font-family: Arial, sans-serif !important;
            }
            body.office-coverage-printing #print-section .office-print-areas-page {
                display: none !important;
            }
            /* ===== Fixed Page Header (repeats on every page) ===== */
            body.office-coverage-printing #print-page-header,
            body.office-coverage-printing #print-page-header * {
                visibility: visible !important;
            }
            body.office-coverage-printing #print-page-header {
                display: flex !important;
                align-items: center !important;
                justify-content: space-between !important;
                position: fixed !important;
                top: 0 !important;
                left: 0 !important;
                width: ${paddedW}px !important;
                height: ${HEADER_H}px !important;
                background: #1e3a5f !important;
                color: #ffffff !important;
                font-family: Arial, sans-serif !important;
                font-size: 13px !important;
                font-weight: 600 !important;
                padding: 0 ${PADDING}px !important;
                box-sizing: border-box !important;
                z-index: 9999 !important;
                border-bottom: 3px solid #3b82f6 !important;
                print-color-adjust: exact !important;
                -webkit-print-color-adjust: exact !important;
            }
            /* ===== Fixed Page Footer (repeats on every page) ===== */
            body.office-coverage-printing #print-page-footer,
            body.office-coverage-printing #print-page-footer * {
                visibility: visible !important;
            }
            body.office-coverage-printing #print-page-footer {
                display: flex !important;
                align-items: center !important;
                justify-content: space-between !important;
                position: fixed !important;
                bottom: 0 !important;
                left: 0 !important;
                width: ${paddedW}px !important;
                height: ${FOOTER_H}px !important;
                background: #f8fafc !important;
                color: #6b7280 !important;
                font-family: Arial, sans-serif !important;
                font-size: 10px !important;
                padding: 0 ${PADDING}px !important;
                box-sizing: border-box !important;
                z-index: 9999 !important;
                border-top: 1px solid #e5e7eb !important;
                print-color-adjust: exact !important;
                -webkit-print-color-adjust: exact !important;
            }
            body.office-coverage-printing #print-areas-root,
            body.office-coverage-printing #print-areas-root * {
                visibility: visible !important;
            }
            body.office-coverage-printing #print-areas-root {
                display: block !important;
                position: relative !important;
                width: ${paddedW}px !important;
                padding: 0 !important;
                background: #ffffff !important;
                z-index: 10 !important;
                color: #111827 !important;
                font-family: Arial, sans-serif !important;
                box-sizing: border-box !important;
            }
            body.office-coverage-printing #print-areas-root .areas-title {
                font-size: 18px !important;
                font-weight: 700 !important;
                margin: 0 0 12px !important;
                color: #111827 !important;
            }
            body.office-coverage-printing #print-areas-root .areas-grid {
                display: grid !important;
                grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
                gap: 2px !important;
            }
            body.office-coverage-printing #print-areas-root .area-item {
                display: flex !important;
                align-items: center !important;
                margin-top: 8px !important;
                gap: 5px !important;
                border: 1px solid #e5e7eb !important;
                border-radius: 4px !important;
                padding: 5px 8px !important;
                break-inside: avoid !important;
                page-break-inside: avoid !important;
                box-sizing: border-box !important;
            }
            body.office-coverage-printing #print-areas-root .area-dot {
                width: 8px !important;
                min-width: 8px !important;
                height: 8px !important;
                border-radius: 50% !important;
                flex: 0 0 auto !important;
                print-color-adjust: exact !important;
                -webkit-print-color-adjust: exact !important;
            }
            body.office-coverage-printing #print-areas-root .area-name {
                font-size: 11px !important;
                color: #374151 !important;
                word-break: break-word !important;
                line-height: 14px !important;
            }
            body.office-coverage-printing #print-section .office-print-areas-title {
                font-size: 18px !important;
                font-weight: 700 !important;
                margin: 0 0 12px !important;
                color: #111827 !important;
            }
            body.office-coverage-printing #print-section .office-print-areas-grid {
                display: grid !important;
                grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
                gap: 6px !important;
            }
            body.office-coverage-printing #print-section .office-print-area-item {
                display: flex !important;
                align-items: center !important;
                gap: 5px !important;
                border: 1px solid #e5e7eb !important;
                border-radius: 4px !important;
                padding: 5px 8px !important;
                break-inside: avoid !important;
                page-break-inside: avoid !important;
            }
            body.office-coverage-printing #print-section .office-print-area-dot {
                width: 8px !important;
                height: 8px !important;
                border-radius: 50% !important;
                flex: 0 0 auto !important;
                print-color-adjust: exact !important;
                -webkit-print-color-adjust: exact !important;
            }
            body.office-coverage-printing #print-section .office-print-area-name {
                font-size: 11px !important;
                color: #374151 !important;
                word-break: break-word !important;
                line-height: 14px !important;
            }
            body.office-coverage-printing #print-section .office-print-legend-title {
                font-size: 20px !important;
                font-weight: 700 !important;
                margin: 0 0 5px !important;
            }
            body.office-coverage-printing #print-section .office-print-legend-grid {
                margin-top: 4px !important;
                width: fit-content !important;
                display: grid !important;
                grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
                gap: 6px !important;
            }
            body.office-coverage-printing #print-section .office-print-legend-item {
                display: flex !important;
                align-items: flex-start !important;
                gap: 6px !important;
                width: 240px !important;
                box-sizing: border-box !important;
                border: 1px solid #d1d5db !important;
                border-radius: 4px !important;
                padding: 7px 8px !important;
                break-inside: avoid !important;
                page-break-inside: avoid !important;
                margin-bottom: 0 !important;
            }
            body.office-coverage-printing #print-section .office-print-color-box {
                width: 12px !important;
                height: 12px !important;
                border: 1px solid #6b7280 !important;
                border-radius: 2px !important;
                flex: 0 0 auto !important;
                print-color-adjust: exact !important;
                -webkit-print-color-adjust: exact !important;
                margin-bottom: 5px !important;
            }
            body.office-coverage-printing #print-section .office-print-office-name {
                font-size: 12px !important;
                line-height: 13px !important;
                font-weight: 400 !important;
                word-break: break-word !important;
            }
            body.office-coverage-printing #print-section .office-print-single-header {
                display: flex !important;
                align-items: center !important;
                gap: 8px !important;
                margin-bottom: 10px !important;
                margin-top: 10px !important;
            }
            body.office-coverage-printing #print-section .office-print-single-office-name {
                font-size: 16px !important;
                font-weight: 700 !important;
                margin: 0 !important;
            }
            body.office-coverage-printing #print-section .office-print-single-grid {
                display: grid !important;
                grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
                gap: 8px !important;
                margin-top: 4px !important;
            }
            body.office-coverage-printing #print-section .office-print-overflow-grid {
                display: none !important;
            }
            body.office-coverage-printing #print-section .office-print-single-card {
                border: 1px solid #d1d5db !important;
                border-radius: 6px !important;
                padding: 10px 12px !important;
                box-sizing: border-box !important;
                break-inside: avoid !important;
                page-break-inside: avoid !important;
            }
            body.office-coverage-printing #print-section .office-print-single-card-title {
                font-size: 11px !important;
                font-weight: 600 !important;
                color: #6b7280 !important;
                text-transform: uppercase !important;
                letter-spacing: 0.5px !important;
                margin: 0 0 4px !important;
            }
            body.office-coverage-printing #print-section .office-print-single-card-value {
                font-size: 12px !important;
                color: #111827 !important;
                margin: 0 !important;
                word-break: break-word !important;
            }
            /* ===== Overflow addresses rendered in print-areas-root on page 2 ===== */
            body.office-coverage-printing #print-areas-root .overflow-addresses-section {
                margin-bottom: 16px !important;
            }
            body.office-coverage-printing #print-areas-root .overflow-addresses-section .office-print-single-grid {
                display: grid !important;
                grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
                gap: 8px !important;
                margin-top: 4px !important;
            }
            body.office-coverage-printing #print-areas-root .overflow-addresses-section .office-print-single-card {
                border: 1px solid #d1d5db !important;
                border-radius: 6px !important;
                padding: 10px 12px !important;
                box-sizing: border-box !important;
                break-inside: avoid !important;
                page-break-inside: avoid !important;
            }
            body.office-coverage-printing #print-areas-root .overflow-addresses-section .office-print-single-card-title {
                font-size: 11px !important;
                font-weight: 600 !important;
                color: #6b7280 !important;
                text-transform: uppercase !important;
                letter-spacing: 0.5px !important;
                margin: 0 0 4px !important;
            }
            body.office-coverage-printing #print-areas-root .overflow-addresses-section .office-print-single-card-value {
                font-size: 12px !important;
                color: #111827 !important;
                margin: 0 !important;
                word-break: break-word !important;
            }
            body.office-coverage-printing #print-areas-root .overflow-addresses-section .overflow-title {
                font-size: 14px !important;
                font-weight: 700 !important;
                margin: 0 0 8px !important;
                color: #111827 !important;
            }
        }
    `;

        let isCleanedUp = false;
        const cleanupPrintDom = () => {
            if (isCleanedUp) return;
            isCleanedUp = true;
            document.getElementById('print-style')?.remove();
            document.getElementById('print-areas-root')?.remove();
            document.getElementById('print-page-header')?.remove();
            document.getElementById('print-page-footer')?.remove();
            document.body.classList.remove('office-coverage-printing');
            window.removeEventListener('afterprint', cleanupPrintDom);
            window.dispatchEvent(new Event('resize'));
        };

        document.head.appendChild(style);
        document.body.classList.add('office-coverage-printing');
        window.addEventListener('afterprint', cleanupPrintDom);

        // Inject fixed page header (appears on every printed page)
        document.getElementById('print-page-header')?.remove();
        const pageHeader = document.createElement('div');
        pageHeader.id = 'print-page-header';
        pageHeader.innerHTML = `
            <span style="font-size:13px;font-weight:700;color:#ffffff;letter-spacing:0.3px;">${headerTitle}</span>
        `;
        document.body.appendChild(pageHeader);

        // Inject fixed page footer (appears on every printed page)
        document.getElementById('print-page-footer')?.remove();
        const pageFooter = document.createElement('div');
        pageFooter.id = 'print-page-footer';
        pageFooter.innerHTML = `
            <span>JB Glass</span>
            <span>Printed on: ${printedAt}</span>
        `;
        document.body.appendChild(pageFooter);

        // Inject page-2 areas content directly into body (normal flow, so margin-top pushes it to page 2)
        // Replace from: document.getElementById('print-areas-root')?.remove();
        // To the closing brace of the if (selectedOfficesFromDDL.length === 1) block

        document.getElementById('print-areas-root')?.remove();
        if (selectedOfficesFromDDL.length === 1) {
            const singleOfficeId = selectedOfficesFromDDL[0];
            const singleOfficeName = officesForLegend.find(o => o.officeId === singleOfficeId)?.officeName || '';
            const officeColor = generateOfficeColor(singleOfficeId).fill;
            // Group territories by placeType for print
            const exportGroupOrder = [
                { type: GoogleMapPlaceTypeEnumEntity.COUNTRY, label: 'Country', nameKey: (r: any) => r.countryName ?? null },
                { type: GoogleMapPlaceTypeEnumEntity.STATE, label: 'State', nameKey: (r: any) => r.stateName ?? null },
                { type: GoogleMapPlaceTypeEnumEntity.DIVISION, label: 'Division', nameKey: (r: any) => r.divisionName ?? null },
                { type: GoogleMapPlaceTypeEnumEntity.CITY, label: 'City', nameKey: (r: any) => r.cityName ?? null },
            ];
            const exportEntries = officeRegionDetails.filter(r => r.officeId === singleOfficeId);
            const exportTerritoryGroups: { label: string; names: string[] }[] = [];
            exportGroupOrder.forEach(({ type, label, nameKey }) => {
                const names = Array.from(new Set(
                    exportEntries.filter(r => r.placeType === type).map(nameKey).filter(Boolean) as string[]
                ));
                if (names.length > 0) exportTerritoryGroups.push({ label, names });
            });
            const areaNames = exportTerritoryGroups.flatMap(g => g.names);

            // Max addres on page 1
            const MAX_ADDRESSES_PAGE1 = 4;
            const singleAddresses = filteredOfficeAddresses.filter(a => a.officeId === singleOfficeId);
            const overflowAddresses = singleAddresses.slice(MAX_ADDRESSES_PAGE1);

            const isSpecialCase = singleAddresses.length <= 2 && areaNames.length < 8;
            const hasOverflowAddresses = overflowAddresses.length > 0;
            const hasAreas = areaNames.length > 0 && !isSpecialCase && showTerritories;

            if (hasOverflowAddresses || hasAreas) {
                const PAGE_H = 1123;           // px height of one A4 page at 96dpi
                const HEADER_H = 44;           // fixed header height
                const FOOTER_H = 32;           // fixed footer height
                const ITEM_H = 36;             // approx height per area item (px) — tune if needed
                const TITLE_H = 40;            // h2 title height
                const ITEM_GAP = 2;            // gap between items in grid
                const COLS = 4;                // grid columns

                const paddedAreas: string[] = [];
                exportTerritoryGroups.forEach(group => {
                    const paddedCount = Math.ceil(group.names.length / COLS) * COLS;
                    group.names.forEach(name => paddedAreas.push(name));
                    for (let i = group.names.length; i < paddedCount; i++) {
                        paddedAreas.push('\x00'); // padding sentinel — never rendered
                    }
                });

                // Estimate height taken by overflow addresses on page 2
                const OVERFLOW_CARD_H = 120;   // approx height per address card row (2 cards per row)
                const OVERFLOW_TITLE_H = 36;   // title "Additional Addresses" height
                const overflowRows = Math.ceil(overflowAddresses.length / 2);
                const overflowSectionH = hasOverflowAddresses
                    ? OVERFLOW_TITLE_H + (overflowRows * OVERFLOW_CARD_H) + 16 // 16px margin-bottom
                    : 0;

                // Page 2: usable area = PAGE_H - HEADER_H - FOOTER_H - paddingTop(24) - paddingBottom(24) - overflow section
                const PAGE2_USABLE = PAGE_H - HEADER_H - FOOTER_H - 24 - overflowSectionH;
                // Page 3+: same but no overflow section
                const PAGE3_USABLE = PAGE_H - HEADER_H - FOOTER_H - 24;

                const rowH = ITEM_H + ITEM_GAP;
                const GROUP_SEP_H = 30; // separator label (~22px) + margin-bottom (8px)
                const groupSepOverhead = exportTerritoryGroups.length * GROUP_SEP_H;
                const page2ContentH = Math.max(PAGE2_USABLE - groupSepOverhead, rowH);
                const page3ContentH = Math.max(PAGE3_USABLE - groupSepOverhead, rowH);

                const rowsPerPage2 = Math.max(1, Math.floor(page2ContentH / rowH));
                const itemsPerPage2 = (rowsPerPage2 * COLS) - (hasOverflowAddresses ? (overflowRows - 1) * 8 : 0);

                const rowsPerPage3 = Math.max(1, Math.floor(page3ContentH / rowH));
                const itemsPerPage3 = rowsPerPage3 * COLS;

                // Split paddedAreas into page chunks (existing math unchanged — just better-shaped input)
                const chunks: string[][] = [];
                if (hasAreas) {
                    if (paddedAreas.length <= itemsPerPage2) {
                        chunks.push(paddedAreas);
                    } else {
                        chunks.push(paddedAreas.slice(0, itemsPerPage2));
                        let remaining = paddedAreas.slice(itemsPerPage2);
                        while (remaining.length > 0) {
                            const chunk = remaining.slice(0, itemsPerPage3);
                            if (chunk.length > 0) {
                                chunks.push(chunk);
                            }
                            remaining = remaining.slice(itemsPerPage3);
                        }
                    }
                }

                const cleanChunks = chunks.filter(chunk => chunk.some(name => name !== '\x00'));
                const areasRoot = document.createElement('div');
                areasRoot.id = 'print-areas-root';

                // Build overflow addresses HTML for page 2 (above territories)
                let overflowHtml = '';
                if (hasOverflowAddresses) {
                    overflowHtml = `
                        <div class="overflow-addresses-section">
                            <p class="overflow-title"></p>
                            <div class="office-print-single-grid">
                                ${overflowAddresses.map(addr => `
                                    <div class="office-print-single-card">
                                        <p class="office-print-single-card-title">${addr.addressType || ''}</p>
                                        ${addr.godownName ? `<p class="office-print-single-card-value"><strong>Showroom:</strong> ${addr.godownName}</p>` : ''}
                                        <p class="office-print-single-card-value"><strong>Address:</strong> ${addr.address || 'N/A'}</p>
                                        ${addr.addressType === OfficeMarkerAddressTypeEnumEntity.BILLING_ADDRESS &&
                            addr.officeContactNumber
                            ? `<p class="office-print-single-card-value"><strong>Contact:</strong> ${addr.officeContactNumber}</p>`
                            : ""
                        }
                                                        ${addr.addressType === OfficeMarkerAddressTypeEnumEntity.BILLING_ADDRESS &&
                            addr.officeEmail
                            ? `<p class="office-print-single-card-value"><strong>Email:</strong> ${addr.officeEmail}</p>`
                            : ""
                        }
                                    </div>
                                `).join('')}
                            </div>
                        </div>
                    `;
                }

                cleanChunks.forEach((chunk, pageIdx) => {
                    const pageDiv = document.createElement('div');
                    pageDiv.className = 'areas-page-chunk';

                    if (pageIdx === 0) {
                        pageDiv.style.cssText = `
                    box-sizing: border-box;
                    width: 100%;
                    padding: 24px;
                    padding-top: ${HEADER_H + 16}px;
                    padding-bottom: ${FOOTER_H + 24}px;
                    min-height: ${PAGE_H - printMapH - PADDING - 18}px;
                `;
                    } else {
                        pageDiv.style.cssText = `
                    box-sizing: border-box;
                    width: 100%;
                    padding: 24px;
                    padding-top: ${HEADER_H + 16}px;
                    padding-bottom: ${FOOTER_H + 24}px;
                    page-break-before: always;
                    break-before: page;
                `;
                    }

                    const isLastChunk = pageIdx === cleanChunks.length - 1;

                    let emptySlotPadding = 0;
                    if (isLastChunk) {
                        const maxItemsThisPage = pageIdx === 0 ? itemsPerPage2 : itemsPerPage3;
                        const filledRows = Math.ceil(chunk.length / COLS);
                        const maxRows = Math.floor(maxItemsThisPage / COLS);
                        const emptyRows = maxRows - filledRows;
                        emptySlotPadding = emptyRows * rowH;
                    }

                    // On the first areas page (pageIdx=0), prepend overflow addresses above territories
                    pageDiv.innerHTML = `
                ${pageIdx === 0 ? overflowHtml : ''}
                ${exportTerritoryGroups.map(group => `
                    <div style="margin-bottom:8px;">
                        <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.6px;color:#6b7280;margin-bottom:4px;padding-bottom:2px;border-bottom:1px solid #e5e7eb;">${group.label}</div>
                        <div class="areas-grid">
                            ${group.names.filter(name => chunk.includes(name)).map(name => `
                                <div class="area-item">
                                    <span class="area-name">${name}</span>
                                </div>
                            `).join('')}
                        </div>
                    </div>
                `).filter(html => html.includes('area-item')).join('')}
                 ${isLastChunk && emptySlotPadding > 0
                            ? `<div style="height:${emptySlotPadding}px;"></div>`
                            : ''
                        }
            `;
                    areasRoot.appendChild(pageDiv);
                });

                if (hasOverflowAddresses && cleanChunks.length === 0) {
                    const ADDR_COLS = 2;           // 2 cards per row
                    const ADDR_CARD_H = 180;       // approx height per card row (px)
                    const addressesPerPage = 10;
                    const addressChunks: typeof overflowAddresses[] = [];
                    for (let i = 0; i < overflowAddresses.length; i += addressesPerPage) {
                        addressChunks.push(overflowAddresses.slice(i, i + addressesPerPage));
                    }

                    addressChunks.forEach((addrChunk, pageIdx) => {
                        const pageDiv = document.createElement('div');
                        pageDiv.className = 'areas-page-chunk';

                        if (pageIdx === 0) {
                            pageDiv.style.cssText = `
                                box-sizing: border-box;
                                width: 100%;
                                padding: 24px;
                                padding-top: ${HEADER_H + 16}px;
                                padding-bottom: ${FOOTER_H + 24}px;
                                min-height: ${PAGE_H - printMapH - PADDING - 18}px;
                            `;
                        } else {
                            pageDiv.style.cssText = `
                                box-sizing: border-box;
                                width: 100%;
                                padding: 24px;
                                padding-top: ${HEADER_H + 16}px;
                                padding-bottom: ${FOOTER_H + 24}px;
                                page-break-before: always;
                                break-before: page;
                            `;
                        }

                        const isLastChunk = pageIdx === addressChunks.length - 1;
                        const isFirstAddressPage = pageIdx === 0;

                        let emptySlotPadding = 0;
                        if (isLastChunk) {
                            const maxCardsThisPage = addressesPerPage;
                            const filledRows = Math.ceil(addrChunk.length / ADDR_COLS);
                            const maxRows = Math.ceil(maxCardsThisPage / ADDR_COLS);
                            const emptyRows = maxRows - filledRows;
                            emptySlotPadding = emptyRows * ADDR_CARD_H;
                        }

                        pageDiv.innerHTML = `
                            <div class="overflow-addresses-section">
                                <div class="office-print-single-grid">
                                    ${addrChunk.map(addr => `
                                        <div class="office-print-single-card">
                                            <p class="office-print-single-card-title">${addr.addressType || ''}</p>
                                            ${addr.godownName ? `<p class="office-print-single-card-value"><strong>Showroom:</strong> ${addr.godownName}</p>` : ''}
                                            <p class="office-print-single-card-value"><strong>Address:</strong> ${addr.address || 'N/A'}</p>
                                            ${addr.addressType === OfficeMarkerAddressTypeEnumEntity.BILLING_ADDRESS &&
                                addr.officeContactNumber
                                ? `<p class="office-print-single-card-value"><strong>Contact:</strong> ${addr.officeContactNumber}</p>`
                                : ""
                            }
                                            ${addr.addressType === OfficeMarkerAddressTypeEnumEntity.BILLING_ADDRESS &&
                                addr.officeEmail
                                ? `<p class="office-print-single-card-value"><strong>Email:</strong> ${addr.officeEmail}</p>`
                                : ""
                            }
                                        </div>
                                    `).join('')}
                                </div>
                            </div>
                            ${isLastChunk && emptySlotPadding > 0
                                ? `<div style="height:${emptySlotPadding}px;"></div>`
                                : ''
                            }
                        `;
                        areasRoot.appendChild(pageDiv);
                    });
                }

                // areasRoot itself: margin-top pushes it below the map page in print flow
                areasRoot.style.cssText = `
            display: block;
            position: relative;
            margin-top: ${printMapH + PADDING + 18}px;
            width: ${paddedW}px;
            background: #ffffff;
            z-index: 10;
            color: #111827;
            font-family: Arial, sans-serif;
            box-sizing: border-box;
        `;

                document.body.appendChild(areasRoot);
            }
        }

        await waitForMapRender(mapPageElement);
        window.print();
        setTimeout(cleanupPrintDom, 500);
    };

    const officePanelFooterTemplate = () => (
        <div className="flex justify-between p-2">
            <button
                className="p-button p-component p-button-sm"
                onClick={() => {
                    setSelectedOffice(null);
                    setSelectedOfficesFromDDL(tempOffices);
                    officeMultiselectRef.current.hide();
                }}
                disabled={
                    JSON.stringify(tempOffices) === JSON.stringify(selectedOfficesFromDDL)
                }
            >
                Apply
            </button>
        </div>
    );

    const filteredOfficeAddresses = useMemo(() => {
        return officeAddresses.filter(office => {
            const matchesOffice = selectedOfficesFromDDL.includes(office.officeId);

            const matchesType = addressType.includes(office.addressType as OfficeMarkerAddressTypeEnumEntity);

            return matchesOffice && matchesType;
        });
    }, [officeAddresses, selectedOfficesFromDDL, addressType]);

    const selectedOfficeRegions = useMemo(() => {
        if (selectedOfficesFromDDL.length === 0) return [];
        return officeRegionDetails.filter(region => region.officeId && selectedOfficesFromDDL.includes(region.officeId));
    }, [officeRegionDetails, selectedOfficesFromDDL]);

    const getAddressTypeMarkerColor = (type: OfficeMarkerAddressTypeEnumEntity) => {
        const colorMap = {
            [OfficeMarkerAddressTypeEnumEntity.SHOWROOM_ADDRESS]: '#EA4335',
            [OfficeMarkerAddressTypeEnumEntity.BILLING_ADDRESS]: '#1E88E5',
        };

        return colorMap[type] || '#EA4335';
    };

    const getMarkerIcon = (type: OfficeMarkerAddressTypeEnumEntity) => {
        const color = getAddressTypeMarkerColor(type);

        return {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24">
                <path
                    fill="${color}"
                    stroke="#FFFFFF"
                    stroke-width="1"
                    d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
                />
                <circle
                    cx="12"
                    cy="9"
                    r="3"
                    fill="#FFFFFF"
                />
            </svg>
        `)}`,
            scaledSize: new google.maps.Size(40, 40),
            anchor: new google.maps.Point(20, 40),
        };
    };

    return (
        <>
            <Loader isLoading={isLoading || isFetchingOfficeAddress} />

            <div className="content-wrapper">
                <div className="row">
                    <div className="col-12">
                        <div className="card card-rounded">
                            <div className="card-body">
                                <h4 className="card-title">
                                    Office Coverage Overview
                                </h4>
                                <div className="row mb-3">
                                    <div className="col-lg-3 col-md-3 col-sm-12 col-12 form-group">
                                        <label>OFFICES</label>
                                        <MultiSelect
                                            ref={officeMultiselectRef}
                                            value={tempOffices}
                                            onChange={(e) => setTempOffices(e.value)}
                                            options={
                                                officesForLegend.map(office => ({
                                                    officeId: office.officeId,
                                                    officeName: office.officeName,
                                                }))
                                            }
                                            optionLabel="officeName"
                                            optionValue="officeId"
                                            placeholder="Select Offices"
                                            className="w-full md:w-14rem w-100"
                                            maxSelectedLabels={1}
                                            resetFilterOnHide
                                            filter
                                            panelFooterTemplate={officePanelFooterTemplate}
                                            onHide={() => {
                                                setTempOffices(selectedOfficesFromDDL || []);
                                            }}
                                            itemTemplate={(option: any) => {
                                                const isFirstSelected = tempOffices.length > 0 && tempOffices[0] === option.officeId;
                                                const color = isFirstSelected
                                                    ? generateOfficeColor(option.officeId).fill
                                                    : generateOfficeColor(option.officeId).fill;

                                                return (
                                                    <div className="d-flex align-items-end gap-2 mt-1">
                                                        <div
                                                            style={{
                                                                minHeight: "22px",
                                                                minWidth: "22px",
                                                                background: color,
                                                                border: "1px solid #999",
                                                                borderRadius: "3px",
                                                            }}
                                                        />
                                                        <span style={{ lineHeight: "22px" }}>{option.officeName}</span>
                                                    </div>
                                                );
                                            }}
                                        />
                                    </div>

                                    <div className="form-group col-lg-3 col-md-3 col-sm-12 col-12">
                                        <label>OFFICE ADDRESS TYPE</label>
                                        <MultiSelect
                                            ref={addressTypeMultiselectRef}
                                            value={tempAddressType}
                                            onChange={(e) => setTempAddressType(e.value)}
                                            options={[
                                                { name: "Billing Address", value: OfficeMarkerAddressTypeEnumEntity.BILLING_ADDRESS },
                                                { name: "Showroom Address", value: OfficeMarkerAddressTypeEnumEntity.SHOWROOM_ADDRESS },
                                            ]}
                                            optionLabel="name"
                                            optionValue="value"
                                            placeholder="Select Address Type"
                                            className="w-full md:w-14rem w-100"
                                            maxSelectedLabels={1}
                                            resetFilterOnHide
                                            itemTemplate={(option: any) => (
                                                <div className="d-flex align-items-center gap-2 mt-1">
                                                    <span
                                                        style={{
                                                            width: "16px",
                                                            height: "16px",
                                                            borderRadius: "50%",
                                                            background: getAddressTypeMarkerColor(option.value),
                                                            border: "1px solid #ffffff",
                                                            boxShadow: "0 0 0 1px #9ca3af",
                                                            flex: "0 0 auto",
                                                        }}
                                                    />
                                                    <span style={{ lineHeight: "16px" }}>{option.name}</span>
                                                </div>
                                            )}
                                            panelFooterTemplate={() => (
                                                <div className="flex justify-between p-2">
                                                    <button
                                                        className="p-button p-component p-button-sm"
                                                        onClick={() => {
                                                            setSelectedOffice(null);
                                                            setAddressType(tempAddressType);
                                                            addressTypeMultiselectRef.current.hide();
                                                        }}
                                                        disabled={
                                                            JSON.stringify(tempAddressType) === JSON.stringify(addressType)
                                                        }
                                                    >
                                                        Apply
                                                    </button>
                                                </div>
                                            )}
                                            onHide={() => setTempAddressType(addressType)}
                                        />
                                    </div>
                                    <div className="form-group col-lg-3 col-md-3 col-sm-12 col-12">
                                        <label>
                                            SHOW TERRITORIES
                                        </label>
                                        <Dropdown
                                            value={showTerritories}
                                            onChange={(e) => {
                                                setShowTerritories(e.value);
                                            }}
                                            options={
                                                [
                                                    { name: "Yes", value: true },
                                                    { name: "No", value: false }
                                                ]
                                            }
                                            optionLabel="name"
                                            optionValue="value"
                                            name="show"
                                            placeholder="Select"
                                            className="w-full md:w-14rem w-100"
                                        />

                                    </div>
                                    <div className="form-group col-lg-3 col-md-3 col-sm-12 col-12">
                                        <label>&nbsp;</label>
                                        <button onClick={handleExport} className="btn btn-primary w-100">
                                            Print / Export
                                        </button>
                                    </div>
                                </div>
                                <div className="col-md-12">
                                    <h4 className="mb-3 fw-bold">Office Coverage Map</h4>
                                    <div id="print-section" ref={sectionRef}>
                                        <div className="office-print-map-page">
                                            <APIProvider apiKey={apiKey} libraries={['places']}>
                                                <Map
                                                    mapId={mapId}
                                                    defaultCenter={{ lat: 20.5937, lng: 78.9629 }}
                                                    defaultZoom={5}
                                                    mapTypeControl={false}
                                                    tilt={0}
                                                    heading={0}
                                                    style={{ width: '100%', height: 'calc(100vh + 200px)', border: '1px solid black' }}
                                                >
                                                    {selectedOfficeRegions.length > 0 && (
                                                        <FitSelectedOfficeOnMap
                                                            regions={selectedOfficeRegions}
                                                        />
                                                    )}
                                                    {!IsNullOrEmptyArray(filteredOfficeAddresses) && !IsNullOrEmptyArray(officesForLegend) && filteredOfficeAddresses.map((office, index) => (
                                                        <Marker
                                                            key={index}
                                                            position={{ lat: office.latitude, lng: office.longitude }}
                                                            onClick={() => {
                                                                setSelectedOffice(null);
                                                                setTimeout(() => setSelectedOffice(office), 0);
                                                            }}
                                                            icon={getMarkerIcon(office.addressType as OfficeMarkerAddressTypeEnumEntity)}
                                                        />
                                                    ))}
                                                    {selectedOffice && (
                                                        <InfoWindow
                                                            position={{
                                                                lat: selectedOffice.latitude,
                                                                lng: selectedOffice.longitude
                                                            }}
                                                            pixelOffset={[0, -44]}
                                                            onCloseClick={() => setSelectedOffice(null)}
                                                            headerContent={
                                                                <h5 >
                                                                    {selectedOffice.officeName}
                                                                </h5>
                                                            }
                                                        >
                                                            <div style={{ maxWidth: '250px' }}>
                                                                <p>
                                                                    <strong>Address Type:</strong><br />
                                                                    {selectedOffice.addressType}
                                                                </p>
                                                                {selectedOffice.godownName && (
                                                                    <p>
                                                                        <strong>Showroom Name:</strong><br />
                                                                        {selectedOffice.godownName}
                                                                    </p>
                                                                )}
                                                                <p >
                                                                    <strong>Address:</strong><br />
                                                                    {selectedOffice.address}
                                                                </p>
                                                                {selectedOffice.primeContactName && (
                                                                    <p className="mb-2">
                                                                        <strong>Name:</strong><br />
                                                                        {selectedOffice.primeContactName}
                                                                    </p>
                                                                )}
                                                                {(selectedOffice.addressType === OfficeMarkerAddressTypeEnumEntity.BILLING_ADDRESS
                                                                    ? selectedOffice.officeContactNumber
                                                                    : selectedOffice.primeContactNumber) && (
                                                                        <p className="mb-2">
                                                                            <strong>Contact:</strong>
                                                                            <br />
                                                                            {selectedOffice.addressType === OfficeMarkerAddressTypeEnumEntity.BILLING_ADDRESS
                                                                                ? selectedOffice.officeContactNumber
                                                                                : selectedOffice.primeContactNumber}
                                                                        </p>
                                                                    )}

                                                                {(selectedOffice.addressType === OfficeMarkerAddressTypeEnumEntity.BILLING_ADDRESS
                                                                    ? selectedOffice.officeEmail
                                                                    : selectedOffice.primeContactEmail) && (
                                                                        <p className="mb-1">
                                                                            <strong>Email:</strong>
                                                                            <br />
                                                                            {selectedOffice.addressType === OfficeMarkerAddressTypeEnumEntity.BILLING_ADDRESS
                                                                                ? selectedOffice.officeEmail
                                                                                : selectedOffice.primeContactEmail}
                                                                        </p>
                                                                    )}
                                                            </div>
                                                        </InfoWindow>
                                                    )}

                                                    <OfficeCoverageOverviewFeatureLayer
                                                        assignments={officeRegionDetails || []}
                                                        initialSelectedCities={officeRegionDetails.map(place => ({
                                                            officeId: selectedOfficesFromDDL[0] || officesForLegend[0]?.officeId || '',
                                                            city_providerId: place.city_providerId,
                                                            division_providerId: place.division_providerId,
                                                            state_providerId: place.state_providerId,
                                                            country_providerId: place.country_providerId,
                                                            type: place.placeType || 'LOCALITY',
                                                            placeName: place.placeName || ''
                                                        }))}
                                                        isShowTerritory={showTerritories}
                                                        selectedOfficeSet={new Set(selectedOfficesFromDDL)}
                                                    />
                                                </Map>
                                            </APIProvider>
                                        </div>
                                        <div
                                            className="office-print-details-page"
                                            style={{
                                                position: 'absolute',
                                                left: '-100000px',
                                                top: 0,
                                                width: 0,
                                                height: 0,
                                                overflow: 'hidden',
                                                opacity: 0,
                                                pointerEvents: 'none',
                                                zIndex: -1
                                            }}
                                        >
                                            <h2 className="office-print-legend-title">Office Details</h2>

                                            {selectedOfficesFromDDL.length === 1 ? (() => {
                                                const singleOffice = officesForLegend.find(o => o.officeId === selectedOfficesFromDDL[0]);
                                                const singleAddresses = filteredOfficeAddresses.filter(a => a.officeId === selectedOfficesFromDDL[0]);
                                                const MAX_ADDRESSES_PAGE1 = 4;
                                                const page1Addresses = singleAddresses.slice(0, MAX_ADDRESSES_PAGE1);
                                                const overflowAddresses = singleAddresses.slice(MAX_ADDRESSES_PAGE1);
                                                if (!singleOffice) return null;

                                                // Group territories by placeType
                                                const territoryGroups: { label: string; names: string[] }[] = [];
                                                const groupOrder: { type: string; label: string; nameKey: (r: any) => string | null }[] = [
                                                    { type: GoogleMapPlaceTypeEnumEntity.COUNTRY, label: 'Country', nameKey: r => r.countryName ?? null },
                                                    { type: GoogleMapPlaceTypeEnumEntity.STATE, label: 'State', nameKey: r => r.stateName ?? null },
                                                    { type: GoogleMapPlaceTypeEnumEntity.DIVISION, label: 'Division', nameKey: r => r.divisionName ?? null },
                                                    { type: GoogleMapPlaceTypeEnumEntity.CITY, label: 'City', nameKey: r => r.cityName ?? null },
                                                ];
                                                const regionEntries = officeRegionDetails.filter(r => r.officeId === singleOffice.officeId);
                                                groupOrder.forEach(({ type, label, nameKey }) => {
                                                    const names = Array.from(new Set(
                                                        regionEntries.filter(r => r.placeType === type).map(nameKey).filter(Boolean) as string[]
                                                    ));
                                                    if (names.length > 0) territoryGroups.push({ label, names });
                                                });

                                                const allAreaNames = territoryGroups.flatMap(g => g.names);
                                                const isSpecialCase = singleAddresses.length <= 2 && allAreaNames.length < 16;

                                                return (
                                                    <>
                                                        <div className="office-print-single-header">
                                                            <span
                                                                className="office-print-color-box"
                                                                style={{ backgroundColor: generateOfficeColor(singleOffice.officeId).fill, width: '16px', height: '16px' }}
                                                            />
                                                            <p className="office-print-single-office-name">
                                                                {singleOffice.officeName}
                                                            </p>
                                                        </div>
                                                        {/* Page 1: Show only first 4 address cards */}
                                                        <div className="office-print-single-grid">
                                                            {page1Addresses.map((addr, idx) => (
                                                                <div className="office-print-single-card" key={idx}>
                                                                    <p className="office-print-single-card-title">{addr.addressType}</p>
                                                                    {addr.godownName && (
                                                                        <p className="office-print-single-card-value"><strong>Showroom:</strong> {addr.godownName}</p>
                                                                    )}
                                                                    <p className="office-print-single-card-value"><strong>Address:</strong> {addr.address || 'N/A'}</p>
                                                                    {addr.primeContactName && (
                                                                        <p className="office-print-single-card-value"><strong>Name:</strong> {addr.primeContactName}</p>
                                                                    )}
                                                                    {(addr.addressType === OfficeMarkerAddressTypeEnumEntity.BILLING_ADDRESS
                                                                        ? addr.officeContactNumber
                                                                        : addr.primeContactNumber) && (
                                                                            <p className="office-print-single-card-value">
                                                                                <strong>Contact:</strong>{" "}
                                                                                {addr.addressType === OfficeMarkerAddressTypeEnumEntity.BILLING_ADDRESS
                                                                                    ? addr.officeContactNumber
                                                                                    : addr.primeContactNumber}
                                                                            </p>
                                                                        )}

                                                                    {(addr.addressType === OfficeMarkerAddressTypeEnumEntity.BILLING_ADDRESS
                                                                        ? addr.officeEmail
                                                                        : addr.primeContactEmail) && (
                                                                            <p className="office-print-single-card-value">
                                                                                <strong>Email:</strong>{" "}
                                                                                {addr.addressType === OfficeMarkerAddressTypeEnumEntity.BILLING_ADDRESS
                                                                                    ? addr.officeEmail
                                                                                    : addr.primeContactEmail}
                                                                            </p>
                                                                        )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                        {isSpecialCase && allAreaNames.length > 0 && showTerritories && (
                                                            <div className="mt-3">
                                                                <h5 className="fw-bold mb-2" style={{ fontSize: '14px' }}>Territories</h5>
                                                                {territoryGroups.map((group) => (
                                                                    <div key={group.label} className="mb-2">
                                                                        <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', color: '#6b7280', marginBottom: '4px', paddingBottom: '2px', borderBottom: '1px solid #e5e7eb' }}>
                                                                            {group.label}
                                                                        </div>
                                                                        <div className="office-print-areas-grid">
                                                                            {group.names.map((name, idx) => (
                                                                                <div className="office-print-area-item" key={idx}>
                                                                                    <span className="office-print-area-name">{name}</span>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {/* Overflow addresses: hidden in DOM, rendered via handleExport on page 2 */}
                                                        {overflowAddresses.length > 0 && (
                                                            <div className="office-print-overflow-grid" data-overflow-count={overflowAddresses.length}>
                                                                {overflowAddresses.map((addr, idx) => (
                                                                    <div className="office-print-single-card" key={`overflow-${idx}`}>
                                                                        <p className="office-print-single-card-title">{addr.addressType}</p>
                                                                        {addr.godownName && (
                                                                            <p className="office-print-single-card-value"><strong>Showroom:</strong> {addr.godownName}</p>
                                                                        )}
                                                                        <p className="office-print-single-card-value"><strong>Address:</strong> {addr.address || 'N/A'}</p>
                                                                        {addr.primeContactName && (
                                                                            <p className="office-print-single-card-value"><strong>Name:</strong> {addr.primeContactName}</p>
                                                                        )}
                                                                        {(addr.addressType === OfficeMarkerAddressTypeEnumEntity.BILLING_ADDRESS
                                                                            ? addr.officeContactNumber
                                                                            : addr.primeContactNumber) && (
                                                                                <p className="office-print-single-card-value">
                                                                                    <strong>Contact:</strong>{" "}
                                                                                    {addr.addressType === OfficeMarkerAddressTypeEnumEntity.BILLING_ADDRESS
                                                                                        ? addr.officeContactNumber
                                                                                        : addr.primeContactNumber}
                                                                                </p>
                                                                            )}

                                                                        {(addr.addressType === OfficeMarkerAddressTypeEnumEntity.BILLING_ADDRESS
                                                                            ? addr.officeEmail
                                                                            : addr.primeContactEmail) && (
                                                                                <p className="office-print-single-card-value">
                                                                                    <strong>Email:</strong>{" "}
                                                                                    {addr.addressType === OfficeMarkerAddressTypeEnumEntity.BILLING_ADDRESS
                                                                                        ? addr.officeEmail
                                                                                        : addr.primeContactEmail}
                                                                                </p>
                                                                            )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                        <div className="office-print-footer" style={{ fontSize: '11px', color: '#6b7280', textAlign: 'right', marginTop: '16px' }}>
                                                            {/* Will be populated dynamically during export */}
                                                        </div>
                                                    </>
                                                );
                                            })() : (
                                                <div className="office-print-legend-grid">
                                                    {officesForLegend.filter(office => selectedOfficesFromDDL.includes(office.officeId)).map(office => (
                                                        <div className="office-print-legend-item" key={office.officeId}>
                                                            <span
                                                                className="office-print-color-box"
                                                                style={{ backgroundColor: generateOfficeColor(office.officeId).fill }}
                                                            />
                                                            <span className="office-print-office-name">
                                                                {office.officeName}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {/* Page 2 (single office only): all map area names */}
                                        {selectedOfficesFromDDL.length === 1 && (() => {
                                            const singleOfficeId = selectedOfficesFromDDL[0];
                                            const singleOfficeName = officesForLegend.find(o => o.officeId === singleOfficeId)?.officeName || '';
                                            const officeColor = generateOfficeColor(singleOfficeId).fill;
                                            // Group territories by placeType for Page 2
                                            const p2GroupOrder: { type: string; label: string; nameKey: (r: any) => string | null }[] = [
                                                { type: GoogleMapPlaceTypeEnumEntity.COUNTRY, label: 'Country', nameKey: r => r.countryName ?? null },
                                                { type: GoogleMapPlaceTypeEnumEntity.STATE, label: 'State', nameKey: r => r.stateName ?? null },
                                                { type: GoogleMapPlaceTypeEnumEntity.DIVISION, label: 'Division', nameKey: r => r.divisionName ?? null },
                                                { type: GoogleMapPlaceTypeEnumEntity.CITY, label: 'City', nameKey: r => r.cityName ?? null },
                                            ];
                                            const p2Entries = officeRegionDetails.filter(r => r.officeId === singleOfficeId);
                                            const p2TerritoryGroups: { label: string; names: string[] }[] = [];
                                            p2GroupOrder.forEach(({ type, label, nameKey }) => {
                                                const names = Array.from(new Set(
                                                    p2Entries.filter(r => r.placeType === type).map(nameKey).filter(Boolean) as string[]
                                                ));
                                                if (names.length > 0) p2TerritoryGroups.push({ label, names });
                                            });

                                            if (p2TerritoryGroups.length === 0) return null;

                                            return (
                                                <div
                                                    className="office-print-areas-page"
                                                    style={{
                                                        position: 'absolute',
                                                        left: '-100000px',
                                                        top: 0,
                                                        width: 0,
                                                        height: 0,
                                                        overflow: 'hidden',
                                                        opacity: 0,
                                                        pointerEvents: 'none',
                                                        zIndex: -1
                                                    }}
                                                >
                                                    {p2TerritoryGroups.map((group) => (
                                                        <div key={group.label} style={{ marginBottom: '8px' }}>
                                                            <div style={{ fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', color: '#6b7280', marginBottom: '4px', paddingBottom: '2px', borderBottom: '1px solid #e5e7eb' }}>
                                                                {group.label}
                                                            </div>
                                                            <div className="office-print-areas-grid">
                                                                {group.names.map((name, idx) => (
                                                                    <div className="office-print-area-item" key={idx}>
                                                                        <span
                                                                            className="office-print-area-dot"
                                                                            style={{ backgroundColor: officeColor }}
                                                                        />
                                                                        <span className="office-print-area-name">{name}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                                <div className='col-12 mt-3'>
                                    <button type="button" className="btn btn-primary font-weight-medium  "

                                        onClick={() => navigate("/settings")}
                                    >
                                        Back
                                    </button>

                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default OfficeCoverageOverview;
